import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown, Check } from "lucide-react";
import { Reveal, Eyebrow, EASE } from "@/components/site/Reveal";
import { SOLUTIONS_DATA } from "@/data/solutions";
import { SOLUTIONS_EN } from "@/data/solutions-en";
import { useLang } from "@/i18n/LanguageContext";
import Stub from "@/pages/Stub";
import { usePageMeta } from "@/lib/usePageMeta";

const CAL_URL = "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr";

function FaqItem({ item, open, onToggle, index }) {
    return (
        <div className="border-b border-gray-200" data-testid={`faq-item-${index}`}>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                data-testid={`faq-toggle-${index}`}
                className="w-full flex items-center justify-between gap-6 py-6 text-left group"
            >
                <span className="font-display text-base md:text-lg font-medium text-weyb-ink group-hover:text-weyb-yellow-dark transition-colors">
                    {item.q}
                </span>
                <ChevronDown
                    size={18}
                    className={`shrink-0 text-weyb-yellow transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                    aria-hidden="true"
                />
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="overflow-hidden"
                    >
                        <p className="pb-6 text-sm md:text-base leading-relaxed text-gray-600 max-w-3xl">
                            {item.a}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function SolutionDetail() {
    const { slug } = useParams();
    const { lang, t, prefix } = useLang();
    const DATA = lang === "en" ? SOLUTIONS_EN : SOLUTIONS_DATA;
    const solution = DATA.find((s) => s.slug === slug);
    const [openFaq, setOpenFaq] = useState(0);
    usePageMeta(
        solution ? solution.seoTitle || `${solution.title} — WEYB` : "WEYB",
        solution?.seoDescription || solution?.tagline
    );

    if (!solution) return <Stub title="Solution" />;

    const idx = DATA.indexOf(solution);
    const next = DATA[(idx + 1) % DATA.length];
    const dark = !!solution.dark;

    const faqLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: solution.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };

    return (
        <div data-testid={`solution-page-${solution.slug}`}>
            <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
            <script type="application/ld+json">{JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: lang === "en" ? "Home" : "Accueil", item: "https://weyb.fr/" },
                    { "@type": "ListItem", position: 2, name: "Solutions", item: "https://weyb.fr/solutions" },
                    { "@type": "ListItem", position: 3, name: solution.title },
                ],
            })}</script>

            {/* Hero */}
            <section className={`pt-32 md:pt-44 pb-20 md:pb-28 ${dark ? "bg-weyb-ink text-white" : "bg-white"}`}>
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <nav aria-label="Fil d'Ariane" className={`flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold mb-10 ${dark ? "text-white/40" : "text-gray-400"}`}>
                            <Link to={prefix || "/"} className="hover:text-weyb-yellow transition-colors" data-testid="breadcrumb-home">{t("solutionDetail.breadcrumbHome")}</Link>
                            <span aria-hidden="true">/</span>
                            <Link to={`${prefix}/solutions`} className="hover:text-weyb-yellow transition-colors" data-testid="breadcrumb-solutions">{t("solutionDetail.breadcrumbSolutions")}</Link>
                            <span aria-hidden="true">/</span>
                            <span className={dark ? "text-weyb-yellow" : "text-weyb-yellow-dark"}>{solution.title}</span>
                        </nav>
                    </Reveal>
                    <div className={`grid grid-cols-1 ${solution.image ? "lg:grid-cols-12 gap-12 items-center" : ""}`}>
                    <div className={solution.image ? "lg:col-span-7" : ""}>
                    <Eyebrow index={solution.num} dark={dark}>{t("solutionDetail.eyebrowLabel")}</Eyebrow>
                    <Reveal delay={0.08}>
                        <h1 className={`font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05] max-w-4xl ${dark ? "text-white" : "text-weyb-ink"}`}>
                            {solution.h1Title || solution.title}
                            <span className="block mt-3 text-xl md:text-2xl font-medium text-weyb-yellow">{solution.heroTagline || solution.tagline}</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <p className={`mt-6 text-base md:text-lg leading-relaxed max-w-2xl ${dark ? "text-white/60" : "text-gray-600"}`}>
                            {solution.heroIntro || solution.heroDesc}
                        </p>
                    </Reveal>
                    <Reveal delay={0.24} className="mt-10 flex flex-wrap gap-4">
                        {solution.externalCta && (
                            <a
                                href={solution.externalCta.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="solution-hero-cta-external"
                                className="inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-7 py-4 rounded-sm text-sm font-semibold transition-colors"
                            >
                                {solution.externalCta.label}
                                <ArrowUpRight size={16} />
                            </a>
                        )}
                        <a
                            href={CAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="solution-hero-cta-booking"
                            className={`inline-flex items-center gap-2 px-7 py-4 rounded-sm text-sm font-semibold transition-colors ${
                                dark
                                    ? "border border-white/25 text-white hover:border-weyb-yellow hover:text-weyb-yellow"
                                    : "bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark"
                            }`}
                        >
                            {t("solutionDetail.ctaBooking")}
                            <ArrowUpRight size={16} />
                        </a>
                        <Link
                            to={`${prefix}/contact`}
                            data-testid="solution-hero-cta-contact"
                            className={`inline-flex items-center gap-2 px-7 py-4 rounded-sm text-sm font-medium transition-colors border ${
                                dark
                                    ? "border-white/25 text-white hover:border-weyb-yellow hover:text-weyb-yellow"
                                    : "border-gray-300 text-weyb-ink hover:border-weyb-yellow"
                            }`}
                        >
                            {t("solutionDetail.ctaContact")}
                        </Link>
                    </Reveal>
                    </div>
                    {solution.image && (
                        <Reveal delay={0.2} className="lg:col-span-5 mt-12 lg:mt-0">
                            <div className="relative max-w-sm lg:max-w-none">
                                <span className="absolute -top-4 -left-4 w-14 h-14 border-t-2 border-l-2 border-weyb-yellow z-10" aria-hidden="true" />
                                <div className="overflow-hidden rounded-sm">
                                    <img
                                        src={solution.image}
                                        alt={solution.imageAlt}
                                        data-testid="solution-hero-image"
                                        className={`w-full ${solution.imageAspect || "aspect-[4/3]"} object-cover`}
                                    />
                                </div>
                            </div>
                        </Reveal>
                    )}
                    </div>
                </div>
            </section>

            {/* Problématique */}
            <section className={`py-20 md:py-28 ${dark ? "bg-white" : "bg-[#F9FAFB] border-y border-gray-100"}`}>
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <Eyebrow>{t("solutionDetail.constatEyebrow")}</Eyebrow>
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight">
                                {t("solutionDetail.constatTitle")}
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1} className="lg:col-span-8">
                        <p className="text-lg md:text-2xl font-light leading-relaxed text-weyb-ink/80">
                            {solution.problematique}
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Pour qui */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow>{t("solutionDetail.pourQuiEyebrow")}</Eyebrow>
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight max-w-2xl">
                            {t("solutionDetail.pourQuiTitle")}
                        </h2>
                    </Reveal>
                    <div className="mt-10 flex flex-wrap gap-3">
                        {solution.pourQui.map((p, i) => (
                            <Reveal key={p} delay={i * 0.05}>
                                <span
                                    data-testid={`pour-qui-${i}`}
                                    className="inline-block border border-gray-200 bg-[#F9FAFB] hover:border-weyb-yellow transition-colors text-sm font-medium text-weyb-ink px-5 py-3 rounded-sm"
                                >
                                    {p}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Ce que WEYB fait */}
            <section className="py-20 md:py-28 bg-weyb-ink text-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow dark>{t("solutionDetail.actionsEyebrow")}</Eyebrow>
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight leading-tight max-w-2xl">
                            {t("solutionDetail.actionsTitle").replace("{n}", solution.actions.length)}
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {solution.actions.map((a, i) => {
                            const CardTag = a.href ? Link : "div";
                            const cardProps = a.href ? { to: `${prefix}${a.href}` } : {};
                            return (
                            <Reveal key={a.title} delay={(i % 3) * 0.06} className="h-full">
                                <CardTag {...cardProps} data-testid={`action-card-${i}`} className="block h-full bg-weyb-ink hover:bg-white/5 p-8 transition-colors">
                                    <span className="font-display text-sm font-medium text-weyb-yellow">{String(i + 1).padStart(2, "0")}</span>
                                    <h3 className="mt-4 font-display text-lg font-medium">{a.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-white/55">{a.desc}</p>
                                </CardTag>
                            </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Livrables */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <Eyebrow>{t("solutionDetail.livrablesEyebrow")}</Eyebrow>
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight">
                                {t("solutionDetail.livrablesTitle")}
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-8">
                        <ul className="space-y-4">
                            {solution.livrables.map((l, i) => (
                                <Reveal key={l} delay={i * 0.05}>
                                    <li data-testid={`livrable-${i}`} className="flex items-start gap-4 border border-gray-200 rounded-sm p-5 hover:border-weyb-yellow transition-colors">
                                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center bg-weyb-yellow rounded-sm">
                                            <Check size={14} strokeWidth={3} className="text-weyb-ink" aria-hidden="true" />
                                        </span>
                                        <span className="text-base font-medium text-weyb-ink">{l}</span>
                                    </li>
                                </Reveal>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 md:py-28 bg-[#F9FAFB] border-y border-gray-100">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <Eyebrow>{t("solutionDetail.faqEyebrow")}</Eyebrow>
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight">
                                {t("solutionDetail.faqTitle")}
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1} className="lg:col-span-8">
                        <div className="border-t border-gray-200">
                            {solution.faq.map((f, i) => (
                                <FaqItem key={f.q} item={f} index={i} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CTA + solution suivante */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="bg-weyb-ink text-white rounded-sm p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
                            <div>
                                <h2 className="font-display text-2xl md:text-4xl font-light tracking-tight">
                                    {t("solutionDetail.ctaTitle")}
                                </h2>
                                <p className="mt-3 text-sm text-white/60 max-w-md">
                                    {t("solutionDetail.ctaText")}
                                </p>
                            </div>
                            <a
                                href={CAL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="solution-final-cta-booking"
                                className="inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-7 py-4 rounded-sm text-sm font-semibold transition-colors shrink-0"
                            >
                                {t("solutionDetail.ctaButton")}
                                <ArrowUpRight size={16} />
                            </a>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1} className="mt-10">
                        <Link
                            to={`${prefix}/solutions/${next.slug}`}
                            data-testid="solution-next-link"
                            className="group flex items-center justify-between border border-gray-200 rounded-sm p-6 hover:border-weyb-yellow transition-colors"
                        >
                            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400">{t("solutionDetail.nextLabel")}</span>
                            <span className="flex items-center gap-3 font-display text-lg font-medium text-weyb-ink">
                                {next.title}
                                <ArrowRight size={17} className="text-weyb-yellow transition-transform group-hover:translate-x-1" />
                            </span>
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
